import { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Table } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { FilterSelect } from "../components/reports/reportShared.jsx";
import { Stars } from "../components/systemData/StarRating.jsx";
import {
  errorMessage,
  getCurrentUser,
  getPhonebook,
  isAbortError,
  listEntityRows,
} from "../services/apiClient.js";
import { formatDate, todayInput } from "../utils/date.js";

// Phonebook (legacy Phonebook.aspx): the company/contact directory —
// contractors (trades, star rating, PL expiry, contact people), assessors,
// assessor/insurer companies — with an email-list builder feeding one mailto.
// 1.0 fixes: the list holds individual addresses (1.0 stored a contractor's
// emails as one ;-joined blob, breaking dedupe/removal); selection is
// client-side, no session state.

const MODES = [
  ["contractors", "Contractors"],
  ["assessors", "Assessors"],
  ["assessor-companies", "Assessor Companies"],
  ["insurer-companies", "Insurer Companies"],
];

// Which System Data entity each mode's rows live in — admin name click-through
// (legacy CDCompanyDetails/PersonDetails popups; Back returns here, like the
// H&S PL alert).
const MODE_ENTITY = {
  contractors: "contractor-companies",
  assessors: "assessors",
  "assessor-companies": "assessor-companies",
  "insurer-companies": "insurer-companies",
};

function PhonebookPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState("contractors");
  const [filters, setFilters] = useState({ contractor_company_id: "", work_centre_id: "", min_rating: "", assessor_company_id: "" });
  const [lookups, setLookups] = useState({});
  const [rows, setRows] = useState(null);
  const [emails, setEmails] = useState(() => new Set());
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState("");
  const [scopedJobNo, setScopedJobNo] = useState(null); // claim_number of ?claim=, from the response

  // Admins: the name clicks through to the record in System Data (edit open);
  // Save/Cancel there returns HERE (the H&S PL-alert deep-link pattern).
  function NameCell({ row }) {
    if (!isAdmin) return <span className="fw-semibold text-nowrap">{row.name}</span>;
    return (
      <span
        role="button"
        className="fw-semibold text-nowrap text-primary text-decoration-underline"
        data-hint="Open in System Data — Back returns to the Phonebook"
        onClick={() => navigate(`/admin/system-data?entity=${MODE_ENTITY[mode]}&edit=${row.id}`)}
      >
        {row.name}
      </span>
    );
  }

  useEffect(() => {
    getCurrentUser().then((me) => setIsAdmin(!!me.is_admin)).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all(
      ["contractor-companies", "work-centres", "assessor-companies"].map((k) =>
        listEntityRows(k).then((r) => [k, r]).catch(() => [k, []])
      )
    ).then((pairs) => setLookups(Object.fromEntries(pairs)));
  }, []);

  // ?claim=<id> — "Open Phonebook" from a job's Orders tab, which narrows the
  // contractor list to the ones on that job (1.0's OrdersPage link). Only
  // meaningful for contractors, so switching to another mode drops it.
  //
  // The id is validated with the SAME rule the service uses (positive integer).
  // Trusting any non-empty string meant "?claim=abc" showed the full contractor
  // list under a banner claiming it was filtered to a job — the server quietly
  // ignored the id and the page said otherwise.
  const claimParam = Number(searchParams.get("claim"));
  const claimId = Number.isInteger(claimParam) && claimParam > 0 ? claimParam : null;
  const jobScoped = mode === "contractors" && claimId !== null;

  useEffect(() => {
    // Aborted on unmount and on every dep change (CLAUDE.md / architecture §11).
    // Without it, clicking "Show all contractors" while the scoped request is in
    // flight can let the OLDER response land last: a filtered list rendered
    // under no banner at all, which is the same lie in the other direction.
    const controller = new AbortController();
    setError("");
    setRows(null);
    getPhonebook({
      mode,
      ...filters,
      ...(jobScoped ? { claim_id: claimId } : {}),
      signal: controller.signal,
    })
      .then((r) => {
        setRows(r.rows);
        setScopedJobNo(r.claim_number ?? null);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setError(errorMessage(err, "Unable to load the phonebook."));
      });
    return () => controller.abort();
  }, [mode, filters, jobScoped, claimId]);

  const set = (name) => (value) => setFilters((f) => ({ ...f, [name]: value }));

  // Every email on a row (contractors have several contact people).
  const rowEmails = (r) =>
    mode === "contractors"
      ? (r.contacts || []).map((c) => c.email).filter(Boolean)
      : [r.email].filter(Boolean);

  function toggle(r, on) {
    setEmails((prev) => {
      const next = new Set(prev);
      for (const e of rowEmails(r)) (on ? next.add(e) : next.delete(e));
      return next;
    });
  }
  const allSelected = (r) => rowEmails(r).length > 0 && rowEmails(r).every((e) => emails.has(e));

  return (
    <section className="page-panel">
      <div className="page-heading"><h2>Phonebook <RulesHelp topic="phonebook" /></h2></div>

      {/* A filtered phonebook that does not SAY it is filtered reads as a
          phonebook missing most of its contractors — and this one can legitimately
          come back empty, on a job with no orders raised yet. So the scope is
          stated, and clearing it is one click. */}
      {jobScoped ? (
        <Alert variant="info" className="py-2 d-flex flex-wrap align-items-center gap-2 no-print">
          {/* Names the JOB, not "this job" — by the time you are reading this
              you have left the claim, and "this job" points at nothing (Robert
              2026-08-11). The number comes back with the ROWS rather than in the
              URL: the link is built on a claim page whose own fetch may not have
              landed, so a number passed through the query string can silently be
              missing. The id is in the URL and the id is enough. */}
          <span>
            Showing only the contractors with orders on{" "}
            <strong>{scopedJobNo ? `job #${scopedJobNo}` : "this job"}</strong>.
          </span>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("claim");
              setSearchParams(next, { replace: true });
            }}
          >
            Show all contractors
          </Button>
        </Alert>
      ) : null}

      <div className="d-flex align-items-end column-gap-3 row-gap-2 flex-wrap mb-3 no-print">
        <div>
          <Form.Label className="mb-1 small text-muted d-block">Show</Form.Label>
          {MODES.map(([v, label]) => (
            <Form.Check
              inline type="radio" key={v} id={`pb-${v}`} name="pb-mode" label={label}
              checked={mode === v} onChange={() => setMode(v)}
            />
          ))}
        </div>
        {mode === "contractors" ? (
          <>
            <FilterSelect label="Contractor" value={filters.contractor_company_id} onChange={set("contractor_company_id")} options={lookups["contractor-companies"]} width={200} />
            <FilterSelect label="Work Centre" value={filters.work_centre_id} onChange={set("work_centre_id")} options={lookups["work-centres"]} width={180} />
            <FilterSelect label="Rating" value={filters.min_rating} onChange={set("min_rating")} width={150}>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Minimum {n} star</option>)}
            </FilterSelect>
          </>
        ) : null}
        {mode === "assessors" ? (
          <FilterSelect label="Assessor Company" value={filters.assessor_company_id} onChange={set("assessor_company_id")} options={lookups["assessor-companies"]} width={200} />
        ) : null}
      </div>

      {/* Email-list builder (legacy session list, now client-side). */}
      <div className="d-flex flex-wrap align-items-center column-gap-3 row-gap-1 border rounded px-3 py-2 mb-3 bg-body-tertiary">
        <span className="small text-muted">Email list: <strong>{emails.size}</strong></span>
        {emails.size > 0 ? (
          <>
            <a className="small" href={`mailto:${[...emails].join(";")}`}>Create email from list</a>
            <Button size="sm" variant="outline-secondary" className="py-0" onClick={() => setEmails(new Set())}>Clear</Button>
            <span className="small text-muted text-truncate" style={{ maxWidth: 600 }} data-hint={[...emails].join("; ")}>
              {[...emails].join("; ")}
            </span>
          </>
        ) : (
          <span className="small text-muted">tick companies below to build a mailing list</span>
        )}
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!rows ? (
        <p className="text-muted">Loading…</p>
      ) : mode === "contractors" ? (
        <Table striped bordered hover responsive size="sm" className="align-middle">
          <thead className="table-surface">
            <tr>
              <th>Name</th>
              <th>Work Centres</th>
              <th>Rating</th>
              <th className="text-nowrap">PL Expiry</th>
              <th>Contacts</th>
              <th>Notes</th>
              <th style={{ width: 40 }} data-hint="Add this company's emails to the list" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td><NameCell row={r} /></td>
                <td className="text-truncate" style={{ maxWidth: 220 }} data-hint={r.work_centres || undefined}>{r.work_centres || "-"}</td>
                <td><Stars value={r.rating} /></td>
                <td className="text-nowrap">
                  {r.pl_expiry ? (
                    <span className={r.pl_expiry <= todayInput() ? "text-danger fw-semibold" : ""}>
                      {formatDate(r.pl_expiry)}
                    </span>
                  ) : (
                    <Badge bg="light" text="secondary" className="border">Not Entered</Badge>
                  )}
                </td>
                <td>
                  {(r.contacts || []).length === 0 ? (
                    <span className="text-muted">-</span>
                  ) : (
                    r.contacts.map((c, i) => (
                      <div key={i} className="small text-nowrap">
                        {c.is_primary ? <strong data-hint="Primary contact">{c.name} ●</strong> : c.name}
                        {c.phone ? <span className="text-muted"> · {c.phone}</span> : null}
                        {c.email ? <> · <a href={`mailto:${c.email}`}>{c.email}</a></> : null}
                      </div>
                    ))
                  )}
                </td>
                <td className="text-truncate" style={{ maxWidth: 180 }} data-hint={r.comments || undefined}>{r.comments || "-"}</td>
                <td className="text-center">
                  {rowEmails(r).length > 0 ? (
                    <Form.Check type="checkbox" checked={allSelected(r)} onChange={(e) => toggle(r, e.target.checked)} />
                  ) : null}
                </td>
              </tr>
            ))}
            {rows.length === 0 ? <tr><td colSpan={7} className="text-center text-muted">No companies.</td></tr> : null}
          </tbody>
        </Table>
      ) : (
        <Table striped bordered hover responsive size="sm" className="align-middle" style={{ maxWidth: 900 }}>
          <thead className="table-surface">
            <tr>
              <th>Name</th>
              {mode === "assessors" ? <th>Company</th> : null}
              <th>Phone</th>
              <th>Email</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td><NameCell row={r} /></td>
                {mode === "assessors" ? <td className="text-nowrap">{r.company_name || "-"}</td> : null}
                <td className="text-nowrap">{r.phone || "-"}</td>
                <td>{r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : <span className="text-muted">-</span>}</td>
                <td className="text-center">
                  {r.email ? (
                    <Form.Check type="checkbox" checked={emails.has(r.email)} onChange={(e) => toggle(r, e.target.checked)} />
                  ) : null}
                </td>
              </tr>
            ))}
            {rows.length === 0 ? <tr><td colSpan={5} className="text-center text-muted">No records.</td></tr> : null}
          </tbody>
        </Table>
      )}
    </section>
  );
}

export default PhonebookPage;
